import React from 'react';
import { Bot, Check, CheckCircle, Clock, Copy, RotateCcw, Save, Square, X } from 'lucide-react';

type Plan = {
  id: number;
  version_number: string;
  updated_at: string;
  is_final: boolean;
  created_by?: number;
  created_by_username?: string;
};

type User = {
  id: number;
  username: string;
  email: string;
  name: string;
} | null;

type Props = {
  content: string;
  onChange: (val: string) => void;
  onSave: () => void;
  user?: User;
  isContentChanged?: boolean;
  isCurrentUserOwner?: boolean;
  plans?: Plan[];
  currentPlanId?: number | null;
  onPlanChange?: (planId: number) => void;
  onDeletePlan?: (planId: number) => void;
  isSyncing?: boolean;
  isCollapsed?: boolean;
  isLeftPanelCollapsed?: boolean;
  onGenerateWithAgents?: () => void;
  onStopAgentGeneration?: () => void;
  isAgentGenerating?: boolean;
  hasMultiAgentProgress?: boolean;
  onContinueAgentGeneration?: () => void;
  onRestartAgentGeneration?: () => void;
};

const Toast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => (
  <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 rounded-lg px-4 py-3 text-white shadow-xl transition-all animate-in slide-in-from-right-10 duration-300 ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
    {type === 'success' ? <Check size={18} /> : <X size={18} />}
    <span className="text-sm font-medium">{message}</span>
    <button onClick={onClose} className="ml-2 rounded p-1 hover:bg-white/20">
      <X size={14} />
    </button>
  </div>
);

export const DocumentEditor: React.FC<Props> = ({
  content,
  onChange,
  onSave,
  isContentChanged = false,
  isCurrentUserOwner = false,
  plans = [],
  currentPlanId = 0,
  onPlanChange,
  isSyncing = false,
  isCollapsed = false,
  onGenerateWithAgents,
  onStopAgentGeneration,
  isAgentGenerating = false,
  hasMultiAgentProgress = false,
  onContinueAgentGeneration,
  onRestartAgentGeneration,
}) => {
  const [toast, setToast] = React.useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showNotification = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const safePlans = Array.isArray(plans)
    ? [...plans].sort((a, b) => parseFloat(b.version_number) - parseFloat(a.version_number))
    : [];

  const currentPlan = safePlans.find(plan => plan.id === currentPlanId);
  const hasVersionControls = !!onPlanChange;
  const canRunAgents = !!currentPlanId && !!isCurrentUserOwner && !isSyncing;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      if (!isSyncing && safePlans.length > 0) {
        onSave();
      }
    }
  };

  const renderToolbar = () => (
    <div className="min-w-max border-b bg-white p-3 shadow-sm">
      <div className="flex h-9 items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {hasVersionControls && (
            <div className="relative">
              <select
                value={currentPlanId || ''}
                onChange={(e) => onPlanChange?.(parseInt(e.target.value, 10))}
                disabled={isSyncing || !onPlanChange || safePlans.length === 0 || isAgentGenerating}
                className={`
                  min-w-[250px] appearance-none cursor-pointer rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${(!onPlanChange || safePlans.length === 0) ? 'border-gray-200 bg-gray-100 text-gray-400' : 'border-gray-300 bg-white text-gray-700'}
                `}
              >
                {safePlans.length > 0 ? (
                  safePlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.version_number} - {plan.is_final ? '定稿' : '草稿'} - {plan.created_by_username || '未知用户'}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>暂无历史版本</option>
                )}
              </select>
            </div>
          )}

          {(onGenerateWithAgents || onStopAgentGeneration || onContinueAgentGeneration || onRestartAgentGeneration) && (
            <>
              {isAgentGenerating ? (
                <button
                  onClick={onStopAgentGeneration}
                  disabled={!onStopAgentGeneration}
                  className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 shadow-sm transition-colors hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95 whitespace-nowrap"
                >
                  <Square size={14} />
                  停止生成
                </button>
              ) : hasMultiAgentProgress ? (
                <>
                  <button
                    onClick={onContinueAgentGeneration}
                    disabled={!onContinueAgentGeneration || !canRunAgents}
                    className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-600 shadow-sm transition-colors hover:border-green-300 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95 whitespace-nowrap"
                  >
                    <Bot size={14} />
                    继续生成
                  </button>
                  <button
                    onClick={onRestartAgentGeneration}
                    disabled={!onRestartAgentGeneration || !canRunAgents}
                    className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-600 shadow-sm transition-colors hover:border-amber-300 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95 whitespace-nowrap"
                  >
                    <RotateCcw size={14} />
                    从头开始
                  </button>
                </>
              ) : (
                <button
                  onClick={onGenerateWithAgents}
                  disabled={!onGenerateWithAgents || !canRunAgents}
                  className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-600 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95 whitespace-nowrap"
                >
                  <Bot size={14} />
                  多Agent一键生成教案
                </button>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-4 pl-4 pr-6">
          <span className="hidden whitespace-nowrap font-mono text-xs text-gray-400 sm:inline-block">
            {content.length} chars
          </span>

          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(content);
                showNotification('已复制到剪贴板', 'success');
              } catch (err) {
                showNotification('复制失败，请手动复制', 'error');
              }
            }}
            disabled={isSyncing || !content}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-transparent bg-gray-500 px-4 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:border-gray-700 hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95"
            title="复制全部内容到剪贴板"
          >
            <Copy size={14} />
          </button>

          {isSyncing ? (
            <span className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-blue-500 animate-pulse">
              <RotateCcw size={12} className="animate-spin" /> 同步中...
            </span>
          ) : currentPlan?.is_final ? (
            <span className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-green-100 bg-green-50 px-2 py-0.5 text-xs font-bold text-green-600">
              <CheckCircle size={12} /> 定稿状态
            </span>
          ) : safePlans.length > 0 && currentPlanId ? (
            <span className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-amber-100 bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-600">
              <Clock size={12} /> 草稿状态
            </span>
          ) : null}

          <button
            onClick={onSave}
            disabled={
              isSyncing ||
              isAgentGenerating ||
              (hasVersionControls && safePlans.length === 0) ||
              !isContentChanged ||
              !isCurrentUserOwner ||
              currentPlan?.is_final === true
            }
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-transparent bg-brand-600 px-4 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:border-brand-800 hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95"
            title="Ctrl+S to save"
          >
            <Save size={14} /> 保存
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`relative flex h-full flex-col overflow-hidden bg-gray-50 transition-all duration-300 ${
      isCollapsed ? 'pointer-events-none w-0 flex-none border-none opacity-0' : 'flex-1 border-l border-gray-200'
    }`}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {renderToolbar()}

      <textarea
        value={content}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isSyncing || isAgentGenerating || (hasVersionControls && safePlans.length === 0) || !isCurrentUserOwner}
        className="m-4 flex-1 resize-none rounded-xl border border-gray-200 bg-white p-8 font-mono text-sm leading-relaxed text-gray-800 shadow-sm outline-none transition-all focus:border-brand-300 focus:ring-4 focus:ring-brand-50 disabled:bg-gray-50 disabled:text-gray-400"
        placeholder={hasVersionControls && safePlans.length === 0 ? '当前没有可编辑版本，请先选择一个可用教案版本。' : '在此处编辑教案内容（按 Ctrl+S 保存）...'}
        spellCheck={false}
      />
    </div>
  );
};
